import "./Button.css";

import { forwardRef, MouseEventHandler, ReactNode } from "react";

import { LoadingSpinner } from "../..";

export type ButtonVariant =
  | "primary"
  | "reversedPrimary"
  | "secondary"
  | "tertiary"
  | "quaternary"
  | "quinary"
  | "forestGreen"
  | "circular";
type ButtonSize = "extraExtraSmall" | "extraSmall" | "small" | "smallLarge" | "medium" | "custom";
type ButtonType = "button" | "submit";
type ButtonFontWeight = "default" | "light";
type ButtonCorners = "rounded" | "default";
type IconPosition = "left" | "right";

export interface ButtonProps {
  /**
   * The 'type' attribute on the HTML &lt;input&gt; element used to render the button.
   */
  type?: ButtonType;
  /**
   * The button variant
   */
  variant?: ButtonVariant;
  size?: ButtonSize;
  fontWeight?: ButtonFontWeight;
  corners?: ButtonCorners;
  disabled?: boolean;
  /**
   * Should the button take the full width of the container
   */
  fullWidth?: boolean;
  /**
   * Should the button have a minimum width
   */
  minWidth?: boolean;
  icon?: ReactNode;
  /**
   * The icon position, by default it is set to right
   */
  iconPosition?: IconPosition;
  isLoading?: boolean;
  loadingText?: string;
  onClick?: (() => void | Promise<void>) | MouseEventHandler<HTMLButtonElement>;
  /**
   * Hover handlers, so a wrapping tooltip (e.g. NavigationTooltip) can show on hover
   */
  onMouseEnter?: MouseEventHandler<HTMLButtonElement>;
  onMouseLeave?: MouseEventHandler<HTMLButtonElement>;
  text: string;
  textUnderline?: boolean;
  textBold?: boolean;
  hasNoPadding?: boolean;
  className?: string;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type = "button",
      variant = "primary",
      size = "medium",
      fontWeight = "default",
      corners = "default",
      disabled = false,
      fullWidth = false,
      minWidth = true,
      icon,
      iconPosition = "right",
      isLoading = false,
      loadingText = "Loading",
      onClick,
      onMouseEnter,
      onMouseLeave,
      text,
      textUnderline = false,
      textBold = false,
      hasNoPadding = false,
      className = "",
    },
    ref
  ): JSX.Element => {
    return (
      <button
        ref={ref}
        className={`Button Button_${variant} Button_${size} Button_${fontWeight} Button_content_${size} ${className}
      ${fullWidth ? "Button_fullWidth" : ""} ${!minWidth ? "Button_noMinWidth" : ""} ${icon ? `Button_${iconPosition}Icon` : ""} Button_${corners}Corners ${
        hasNoPadding ? `ButtonNoPadding` : ""
      }${isLoading ? `ButtonLoading` : ""} ${textUnderline ? `ButtonUnderline` : ""} ${textBold ? `ButtonBold` : ""}`}
        type={type}
        disabled={disabled || isLoading}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {isLoading ? (
          <div>
            {loadingText}
            <LoadingSpinner type={variant === "primary" ? "light" : "blue"} />
          </div>
        ) : (
          <>
            {icon}
            <span>{text}</span>
          </>
        )}
      </button>
    );
  }
);
